import { useState } from 'react'
import { X, Plus, Lock, Mail, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { addOwnEmail, removeOwnEmail } from '../own-emails'

interface OwnEmailsContent {
  type: 'own-emails'
  emails: string[]
  loginEmail: string
}

interface Props {
  content: OwnEmailsContent
}

export default function OwnEmailsExpanded({ content }: Props) {
  const { t } = useTranslation('settings')
  const [emails, setEmails] = useState<string[]>(content.emails)
  const [newEmail, setNewEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [removingEmail, setRemovingEmail] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleAdd = async () => {
    const trimmed = newEmail.trim().toLowerCase()
    if (!trimmed || emails.includes(trimmed)) return
    setSaving(true)
    setError(null)
    try {
      const updated = await addOwnEmail(trimmed)
      setEmails(updated)
      setNewEmail('')
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ownEmails.failedToAdd'))
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (email: string) => {
    if (email === content.loginEmail) return
    setRemovingEmail(email)
    setError(null)
    try {
      const updated = await removeOwnEmail(email)
      setEmails(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ownEmails.failedToRemove'))
    } finally {
      setRemovingEmail(null)
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-pearl/50">
        {t('ownEmails.description')}
      </p>

      {error && (
        <p className="text-xs text-rose/70">{error}</p>
      )}

      {/* Email list */}
      {emails.map((email) => {
        const isLogin = email === content.loginEmail
        const isRemoving = removingEmail === email
        return (
          <div key={email} className={`flex items-center justify-between py-1.5 ${isRemoving ? 'opacity-50' : ''}`}>
            <div className="flex items-center gap-2 min-w-0">
              <Mail size={14} className="text-pearl/60 shrink-0" />
              <span className="text-sm text-pearl truncate">{email}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {isLogin ? (
                <Lock size={14} className="text-pearl/55 ml-1" />
              ) : isRemoving ? (
                <Loader2 size={14} className="text-pearl/55 animate-spin ml-1" />
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemove(email)
                  }}
                  disabled={!!removingEmail}
                  className="p-1 rounded hover:bg-rose/10 transition-colors disabled:opacity-50"
                >
                  <X size={14} className="text-pearl/60 hover:text-rose" />
                </button>
              )}
            </div>
          </div>
        )
      })}

      {/* Add email input */}
      <div className="flex items-center gap-2 pt-1">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd() }}
          onClick={(e) => e.stopPropagation()}
          placeholder={t('ownEmails.addPlaceholder')}
          disabled={saving}
          className="flex-1 bg-slate-mid/30 rounded-lg px-3 py-1.5 text-sm text-ivory placeholder:text-pearl/55 outline-none focus:ring-1 focus:ring-gold/40 disabled:opacity-50"
        />
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleAdd()
          }}
          disabled={saving}
          className="p-1.5 rounded-full bg-gold/10 text-gold hover:bg-gold/20 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
        </button>
      </div>
    </div>
  )
}
